// Staff editor for the Broker Commission Modeler's four program numbers at
// /portfolio-admin/commission-modeler-terms. Moves the terms every modeled figure is
// computed against without a redeploy; brokers see them read-only in the portal.
//
// 🔴 Both rates are FRACTIONS on the wire (0.10) and PERCENTS in these inputs
// (10) — ×100 on display, ÷100 on save, each through a toFixed guard so binary
// float drift (0.07 × 100 = 7.000000000000001) never reaches an input or a column.
// The percent is guarded at 4dp because the fraction column is decimal(18,6).
//
// Gated Partners.ManageBrands. The permission's NAME does not describe program
// terms — the reuse is deliberate (it already carries the broker-tooling audience
// C0/C1/C5 + ADMIN); see CommissionModelerTermsController. The route already
// requires it; the guard below is defense-in-depth.

import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Skeleton,
  SubmitButton,
  Timestamp,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import type { CommissionModelerTermsConfigDto } from "@/api/portfolioAdmin/commissionModelerTermsApi";
import {
  useCommissionModelerTerms,
  useUpdateCommissionModelerTerms,
} from "@/hooks/portfolioAdmin/useCommissionModelerTerms";

const MANAGE_PERMISSION = "Partners.ManageBrands";

/** Fraction → percent for display. 4dp on the percent = 6dp on the fraction, which
 *  is exactly the stored column's scale, so nothing is shown that cannot be saved. */
const toPercent = (fraction: number) =>
  Number((fraction * 100).toFixed(4)).toString();

/** Percent → fraction for the wire, rounded to the column's 6dp. */
const toFraction = (percent: number) => Number((percent / 100).toFixed(6));

export default function CommissionModelerTermsPage() {
  const { hasPermission } = useAuth();
  const query = useCommissionModelerTerms();

  if (!hasPermission(MANAGE_PERMISSION)) {
    return (
      <div className="p-space-5">
        <PageHeader eyebrow="Finance" title="Commission modeler terms" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to manage commission modeler terms."
        />
      </div>
    );
  }

  return (
    <div className="p-space-5">
      <PageHeader
        eyebrow="Finance"
        title="Commission modeler terms"
        description="The program terms every figure in a broker's Commission Modeler is computed against. Brokers see these read-only and cannot change them. Saved scenarios store only their inputs and are recomputed on every read, so a change here moves every broker's modeled figures at once — it does not alter any committed deal, loan or payment."
      />

      {query.isLoading ? (
        <Skeleton className="h-64 w-full" />
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="commission-modeler-terms-error"
        >
          Couldn't load commission modeler terms. Try again.
        </p>
      ) : !query.data ? (
        <EmptyState
          title="No terms available"
          description="The program terms could not be resolved."
        />
      ) : (
        <TermsForm config={query.data} />
      )}
    </div>
  );
}

function TermsForm({ config }: { config: CommissionModelerTermsConfigDto }) {
  const toast = useToast();
  const update = useUpdateCommissionModelerTerms();

  const [contractPercent, setContractPercent] = useState(() =>
    toPercent(config.interestRate),
  );
  const [sellerPercent, setSellerPercent] = useState(() =>
    toPercent(config.sellerInterestRate),
  );
  const [amortMonths, setAmortMonths] = useState(() =>
    String(config.amortizationMonths),
  );
  const [liquidityMonth, setLiquidityMonth] = useState(() =>
    String(config.liquidityEventMonth),
  );

  // Re-seed the draft when the server row changes (after a save invalidates and
  // refetches), so the form always shows what is actually in force.
  useEffect(() => {
    setContractPercent(toPercent(config.interestRate));
    setSellerPercent(toPercent(config.sellerInterestRate));
    setAmortMonths(String(config.amortizationMonths));
    setLiquidityMonth(String(config.liquidityEventMonth));
  }, [
    config.interestRate,
    config.sellerInterestRate,
    config.amortizationMonths,
    config.liquidityEventMonth,
  ]);

  const contract = Number(contractPercent);
  const seller = Number(sellerPercent);
  const amort = Number(amortMonths);
  const liquidity = Number(liquidityMonth);

  const blankOrNaN = (raw: string, parsed: number) =>
    raw.trim() === "" || Number.isNaN(parsed);

  // Mirrors CommissionModelerTermsRules server-side, so the operator gets the reason
  // before the round trip. The server is still the authority — it rejects the same
  // inputs with the same reasons if this is ever bypassed.
  const contractError = blankOrNaN(contractPercent, contract)
    ? "Enter a rate."
    : contract < 0 || contract >= 100
      ? "Must be at least 0% and under 100%."
      : null;

  const sellerError = blankOrNaN(sellerPercent, seller)
    ? "Enter a rate."
    : seller < 0 || seller >= 100
      ? "Must be at least 0% and under 100%."
      : !contractError && seller > contract
        ? "The seller's rate can't exceed the contract rate — Phoenix's servicing spread is the difference between them and can't be negative."
        : null;

  const amortError = blankOrNaN(amortMonths, amort)
    ? "Enter a term."
    : !Number.isInteger(amort) || amort <= 0
      ? "Must be a whole number of months greater than zero."
      : null;

  const liquidityError = blankOrNaN(liquidityMonth, liquidity)
    ? "Enter a month."
    : !Number.isInteger(liquidity) || liquidity <= 0
      ? "Must be a whole number of months greater than zero."
      : !amortError && liquidity > amort
        ? "Must not be later than the amortization term."
        : null;

  const invalid = Boolean(
    contractError || sellerError || amortError || liquidityError,
  );

  const spread = contractError || sellerError ? null : contract - seller;

  const save = () => {
    if (invalid) return;
    update.mutate(
      {
        interestRate: toFraction(contract),
        sellerInterestRate: toFraction(seller),
        amortizationMonths: amort,
        liquidityEventMonth: liquidity,
      },
      {
        onSuccess: () => toast.success("Commission modeler terms saved"),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <Card>
      <CardContent className="space-y-space-5">
        <p
          className="text-small text-gray-mid"
          data-testid="commission-modeler-terms-source"
        >
          {config.isOverridden ? (
            <>
              These terms are a saved override.{" "}
              <Timestamp value={config.updatedAt} prefix="Last saved" />
            </>
          ) : (
            "No override is saved — these are the program defaults, the same terms the public prequalification funnel sizes against. Saving here overrides them."
          )}
        </p>

        <div className="grid grid-cols-1 gap-space-4 md:grid-cols-2">
          <Field
            id="modeler-contract-rate"
            label="Contract rate"
            hint="The buyer's rate — what the buyer's payment amortizes at."
            suffix="%"
            error={contractError}
            value={contractPercent}
            onChange={setContractPercent}
            step="0.01"
            testId="commission-modeler-contract-rate"
          />
          <Field
            id="modeler-seller-rate"
            label="Seller rate"
            hint="What the seller's monthly share amortizes at. A percent-of-seller-share commission is a cut of THIS, never of the buyer's payment."
            suffix="%"
            error={sellerError}
            value={sellerPercent}
            onChange={setSellerPercent}
            step="0.01"
            testId="commission-modeler-seller-rate"
          />
          <Field
            id="modeler-amortization"
            label="Amortization term"
            hint="Months. Both schedules amortize over this."
            suffix="months"
            error={amortError}
            value={amortMonths}
            onChange={setAmortMonths}
            step="1"
            testId="commission-modeler-amortization"
          />
          <Field
            id="modeler-liquidity-month"
            label="Modeled through"
            hint="The last month the modeler shows. The ongoing commission accrues up to this month."
            suffix="months"
            error={liquidityError}
            value={liquidityMonth}
            onChange={setLiquidityMonth}
            step="1"
            testId="commission-modeler-liquidity-month"
          />
        </div>

        <p className="text-caption text-gray-mid" data-testid="commission-modeler-spread">
          {spread === null
            ? "Phoenix's servicing spread is the difference between the two rates."
            : `Phoenix's servicing spread is the difference between the two rates — currently ${Number(spread.toFixed(4))}%.`}
        </p>

        <div className="flex justify-end">
          <SubmitButton
            type="button"
            variant="primary"
            pending={update.isPending}
            pendingLabel="Saving…"
            disabled={invalid}
            onClick={save}
            data-testid="commission-modeler-terms-save"
          >
            Save terms
          </SubmitButton>
        </div>
      </CardContent>
    </Card>
  );
}

function Field({
  id,
  label,
  hint,
  suffix,
  error,
  value,
  onChange,
  step,
  testId,
}: {
  id: string;
  label: string;
  hint: string;
  suffix: string;
  error: string | null;
  value: string;
  onChange: (next: string) => void;
  step: string;
  testId: string;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <div className="flex items-center gap-space-2">
        <Input
          id={id}
          type="number"
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          invalid={Boolean(error)}
          className="w-32 text-right tabular-nums"
          data-testid={testId}
        />
        <span className="text-small text-gray-mid">{suffix}</span>
      </div>
      <p className="mt-space-1 text-caption text-gray-mid">{hint}</p>
      {error && (
        <p className="mt-space-1 text-caption text-status-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
